import React, { useState, useEffect } from 'react';
import {
  Users, UserPlus, GraduationCap, School, Shield, Key,
  Search, Filter, RefreshCw, CheckCircle, AlertTriangle,
  Lock, Unlock, ArrowRight, UserCheck, UserX, Download, Eye, Plus, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const UserManagementPage: React.FC = () => {
  const { fetchWithAuth } = useAuth();
  const [activeTab, setActiveTab] = useState<'school' | 'beo' | 'deo' | 'all'>('school');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Password reset modal state
  const [resetModalUser, setResetModalUser] = useState<any | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [activeTab, selectedBlock, statusFilter, page]);

  const fetchUsers = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      let roleParam = activeTab === 'all' ? '' : `&role=${activeTab}`;
      let blockParam = selectedBlock !== 'all' ? `&block=${encodeURIComponent(selectedBlock)}` : '';
      let statusParam = statusFilter !== 'all' ? `&status=${encodeURIComponent(statusFilter)}` : '';
      let searchParam = searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery.trim())}` : '';

      const res = await fetchWithAuth(`/api/admin/users?page=${page}&limit=${limit}${roleParam}${blockParam}${statusParam}${searchParam}`);
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.data) {
          setUsers(json.data.users || []);
          setTotalUsers(json.data.pagination?.total || 0);
          setTotalPages(json.data.pagination?.totalPages || 1);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        setErrorMessage(err.message || 'Failed to fetch user accounts.');
      }
    } catch (e: any) {
      setErrorMessage('Network or server error while loading users.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleToggleStatus = async (user: any) => {
    try {
      const res = await fetchWithAuth(`/api/admin/users/${user.id}/status`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setStatusMessage(`Status for user "${user.username}" updated to ${data.new_status}.`);
        fetchUsers();
      } else {
        setErrorMessage(data.message || 'Failed to toggle status.');
      }
    } catch (e) {
      setErrorMessage('Error toggling user status.');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetModalUser) return;
    if (newPassword.length < 6) {
      setErrorMessage('Password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setResetting(true);
    try {
      const res = await fetchWithAuth(`/api/admin/users/${resetModalUser.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword })
      });
      const data = await res.json();
      if (data.success) {
        setStatusMessage(`Password for user "${resetModalUser.username}" reset successfully.`);
        setResetModalUser(null);
        setNewPassword('');
        setConfirmPassword('');
        fetchUsers();
      } else {
        setErrorMessage(data.message || 'Failed to reset password.');
      }
    } catch (e) {
      setErrorMessage('Error resetting password.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Official Government Header */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-50 text-blue-900 rounded-xl border border-blue-200 shadow-inner">
            <Shield className="w-6 h-6 text-[#0F2C59]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">
                Official User Credential &amp; Account Management
              </h1>
              <span className="bg-amber-100 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider border border-amber-300">
                DEO / Admin Only
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Secure administrative directory for all 161 Schools, 5 Block Education Officers (BEO), and District Education Administrators.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => { setPage(1); fetchUsers(); }}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 text-slate-700 bg-white rounded-lg hover:bg-slate-50 transition-colors text-xs font-semibold shadow-sm"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Alerts */}
      {statusMessage && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-medium">{statusMessage}</span>
          </div>
          <button onClick={() => setStatusMessage('')} className="font-bold text-emerald-700 hover:underline">Dismiss</button>
        </div>
      )}

      {errorMessage && (
        <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-900 rounded-xl text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
            <span className="font-medium">{errorMessage}</span>
          </div>
          <button onClick={() => setErrorMessage('')} className="font-bold text-rose-700 hover:underline">Dismiss</button>
        </div>
      )}

      {/* Quick Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total School Logins</div>
          <div className="text-2xl font-black text-[#0F2C59] mt-1">161 / 161</div>
          <div className="text-[11px] text-emerald-700 font-medium mt-0.5">100% Schools Provisioned</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Block Education Officers</div>
          <div className="text-2xl font-black text-amber-700 mt-1">5 Blocks</div>
          <div className="text-[11px] text-slate-500 font-medium mt-0.5">Nuh, Nagina, Taoru, FPJ, Punhana</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Active System Users</div>
          <div className="text-2xl font-black text-slate-900 mt-1">{totalUsers}</div>
          <div className="text-[11px] text-slate-500 font-medium mt-0.5">Across All Roles &amp; Blocks</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Credential Security</div>
          <div className="text-2xl font-black text-indigo-700 mt-1">Encrypted</div>
          <div className="text-[11px] text-slate-500 font-medium mt-0.5">Argon2 / SHA-256 Hashed</div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Role Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-4 pt-3 gap-2 overflow-x-auto">
          {[
            { id: 'school', label: 'School Users (161)', icon: School },
            { id: 'beo', label: 'BEO Users (5 Blocks)', icon: Shield },
            { id: 'deo', label: 'DEO & Admin Users', icon: Key },
            { id: 'all', label: 'All System Accounts', icon: Users }
          ].map(t => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => { setActiveTab(t.id as any); setPage(1); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold rounded-t-lg transition-colors border-t border-x ${
                  active
                    ? 'bg-white text-[#0F2C59] border-slate-200 border-b-transparent shadow-sm'
                    : 'text-slate-600 border-transparent hover:bg-slate-100'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Filters & Search Bar */}
        <div className="p-4 border-b border-slate-200 bg-white flex flex-col md:flex-row items-center justify-between gap-3">
          <form onSubmit={handleSearchSubmit} className="relative w-full md:w-96">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by school code, school name, username..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] focus:border-[#0F2C59] bg-slate-50 text-slate-900"
            />
          </form>

          <div className="flex items-center gap-2 w-full md:w-auto">
            {/* Block filter */}
            <select
              value={selectedBlock}
              onChange={e => { setSelectedBlock(e.target.value); setPage(1); }}
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium focus:ring-2 focus:ring-[#0F2C59]"
            >
              <option value="all">All Blocks</option>
              <option value="Nuh">Nuh</option>
              <option value="Nagina">Nagina</option>
              <option value="Taoru">Taoru</option>
              <option value="Ferozepur Jhirka">Ferozepur Jhirka</option>
              <option value="Punhana">Punhana</option>
            </select>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg bg-slate-50 text-slate-800 font-medium focus:ring-2 focus:ring-[#0F2C59]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Directory Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-700">
            <thead className="bg-slate-100 text-[11px] font-bold text-slate-700 uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="p-3.5">School Code</th>
                <th className="p-3.5">School Name</th>
                <th className="p-3.5">Block</th>
                <th className="p-3.5">User ID</th>
                <th className="p-3.5">Account Status</th>
                <th className="p-3.5">Last Login</th>
                <th className="p-3.5">Password Status</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-slate-400" />
                    Loading user credentials directory...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    No user accounts found matching selected criteria.
                  </td>
                </tr>
              ) : (
                users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50/80 transition-colors">
                    {/* School Code */}
                    <td className="p-3.5 font-mono font-bold text-[#0F2C59]">
                      {u.school_code || (u.role === 'beo' ? 'BLOCK-HQ' : u.role === 'deo' ? 'DISTRICT-HQ' : '—')}
                    </td>

                    {/* School Name */}
                    <td className="p-3.5 font-semibold text-slate-900 max-w-xs truncate">
                      {u.school_name || u.name}
                    </td>

                    {/* Block */}
                    <td className="p-3.5 font-medium text-slate-700">
                      {u.block || 'District Wide'}
                    </td>

                    {/* User ID */}
                    <td className="p-3.5 font-mono font-semibold text-slate-800 bg-slate-50/50 rounded">
                      {u.username}
                    </td>

                    {/* Account Status */}
                    <td className="p-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        u.account_status === 'active' || u.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                          : 'bg-rose-100 text-rose-800 border border-rose-300'
                      }`}>
                        {u.account_status === 'active' || u.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Last Login */}
                    <td className="p-3.5 text-slate-500 text-[11px]">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}
                    </td>

                    {/* Password Status */}
                    <td className="p-3.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-900 border border-blue-200">
                        <Lock className="w-2.5 h-2.5" />
                        {u.password_status || 'Configured'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="p-3.5 text-right space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => setResetModalUser(u)}
                        className="px-2.5 py-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded hover:bg-indigo-100 transition-colors"
                      >
                        Reset Password
                      </button>

                      <button
                        onClick={() => handleToggleStatus(u)}
                        className={`px-2.5 py-1 text-[11px] font-semibold rounded border transition-colors ${
                          u.status === 'active'
                            ? 'text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100'
                            : 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                        }`}
                      >
                        {u.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="text-xs text-slate-500">
            Showing Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({totalUsers} total accounts)
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {resetModalUser && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full border border-slate-200 overflow-hidden">
            <div className="bg-[#0F2C59] p-4 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-bold">Administrative Password Reset</h3>
              </div>
              <button
                onClick={() => setResetModalUser(null)}
                className="text-slate-300 hover:text-white text-xs font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="p-5 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs space-y-1">
                <div>User ID: <strong className="font-mono text-[#0F2C59]">{resetModalUser.username}</strong></div>
                <div>Name: <strong>{resetModalUser.school_name || resetModalUser.name}</strong></div>
                <div>Role / Block: <span className="uppercase font-bold">{resetModalUser.role}</span> / {resetModalUser.block || 'District'}</div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  New Secure Password
                </label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59]"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setResetModalUser(null)}
                  className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetting}
                  className="px-4 py-2 bg-[#0F2C59] hover:bg-[#1E40AF] text-white rounded-lg text-xs font-bold shadow disabled:opacity-50"
                >
                  {resetting ? 'Resetting...' : 'Confirm Password Reset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
